import { useEffect, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import FileUploader from "../../components/documents/FileUploader"
import { Loader } from "../../components/state/Loader"
import { ProgressionBar } from "../../components/state/ProgressionBar"
import Survey, { Direction } from "../../components/surveys/Survey"
import SurveyListCard from "../../components/surveys/SurveyListCard"
import SurveyModal from "../../components/surveys/SurveyModal"
import { useAuth } from "../../hooks/useAuth"
import {
  useDocumentRequestsQuery,
  useDocumentsQuery
} from "../../queries/useDocuments"
import { usePatient } from "../../queries/usePatients"
import {
  useActiveAndNotCompletedPatientsSurveys,
  useActivePatientSurveys,
  useCompletedPatientsSurveys,
  useSurveys
} from "../../queries/useSurveys"
import { IPatientSurvey } from "../../types/types"
import { useAccountStore } from "../../utils/store"
import { SurgeryCategory } from "../SurgeryCategory"

enum FlowPhase {
  INTRO = "INTRO",
  SURVEYS = "SURVEYS",
  DOCUMENTS = "DOCUMENTS",
  OUTRO = "OUTRO",
  ANSWERS = "ANSWERS",
  STOP = "STOP"
}

const determinePhase = (
  flowPhase: FlowPhase | undefined,
  completedPatientsSurveysLength: number | undefined,
  unAnsweredPatientSurveysLength: number | undefined,
  medicalDocumentsLength: number | undefined,
  medicalDocumentRequestsLength: number | undefined
) => {
  if (
    flowPhase !== FlowPhase.SURVEYS &&
    ((unAnsweredPatientSurveysLength && !medicalDocumentsLength) ||
      !completedPatientsSurveysLength)
  )
    return FlowPhase.INTRO
  if (unAnsweredPatientSurveysLength && !medicalDocumentsLength)
    return FlowPhase.SURVEYS
  if (
    !unAnsweredPatientSurveysLength &&
    (!medicalDocumentsLength || medicalDocumentRequestsLength)
  )
    return FlowPhase.DOCUMENTS
  if (!unAnsweredPatientSurveysLength && medicalDocumentsLength)
    return FlowPhase.OUTRO
  else return FlowPhase.ANSWERS
}

const Home = () => {
  const { t } = useTranslation()
  const scrollRef = useRef<null | HTMLDivElement>(null)
  const { authenticate } = useAuth()
  const { account } = useAccountStore()
  const { data: patient, isLoading: isPatientLoading } = usePatient(
    account?.patient_id
  )

  const {
    data: activePatientSurveys,
    isLoading: isActivePatientSurveysLoading
  } = useActivePatientSurveys(
    account?.patient_id?.toString() || "",
    account?.funnel,
    patient?.surgery_category,
    patient?.recommended
  )

  const { data: completedPatientsSurveys, isLoading: isPatientSurveysLoading } =
    useCompletedPatientsSurveys(
      account?.patient_id?.toString() || "",
      account?.funnel,
      patient?.surgery_category,
      patient?.recommended
    )

  const {
    data: uncompletePatientsSurveys,
    isLoading: isUnAnsweredPatientSurveysLoading
  } = useActiveAndNotCompletedPatientsSurveys(
    account?.patient_id?.toString() || "",
    account?.funnel,
    patient?.surgery_category,
    patient?.recommended
  )
  const { data: surveysDefinition, isLoading: isSurveysLoading } = useSurveys()
  const { data: medicalDocuments, isLoading: isMedicalDocumentsLoading } =
    useDocumentsQuery(account?.patient_id?.toString() || "")
  const {
    data: medicalDocumentRequests,
    isLoading: isMdicalDocumentRequestsLoading
  } = useDocumentRequestsQuery(account?.patient_id?.toString() || "")

  const [showSurveyModal, setShowSurveyModal] = useState(false)
  const [patientSurvey, setPatientSurvey] = useState<IPatientSurvey>()
  const [flowPhase, setFlowPhase] = useState<FlowPhase>()

  const [patientSurveyVisible, setPatientSurveyVisible] = useState<number>(0)
  const [completion, setCompletion] = useState<number>(0)
  const [
    completedPatientsSurveysIndexDisplayed,
    setCompletedPatientsSurveysIndexDisplayed
  ] = useState<number>((completedPatientsSurveys?.length ?? 1) - 1)

  useEffect(() => {
    if (
      !(
        isPatientSurveysLoading ||
        isSurveysLoading ||
        isMedicalDocumentsLoading ||
        isUnAnsweredPatientSurveysLoading ||
        isMdicalDocumentRequestsLoading
      )
    )
      setFlowPhase(
        determinePhase(
          flowPhase,
          completedPatientsSurveys?.length,
          uncompletePatientsSurveys?.length,
          medicalDocuments?.length,
          medicalDocumentRequests?.length
        )
      )
  }, [
    completedPatientsSurveys,
    uncompletePatientsSurveys,
    medicalDocuments,
    isPatientSurveysLoading,
    isSurveysLoading,
    isMedicalDocumentsLoading,
    isUnAnsweredPatientSurveysLoading,
    isMdicalDocumentRequestsLoading,
    medicalDocumentRequests?.length
  ])

  useEffect(() => {
    if (!isPatientSurveysLoading && completedPatientsSurveys)
      setCompletedPatientsSurveysIndexDisplayed(
        completedPatientsSurveys?.length
      )
  }, [isPatientSurveysLoading, completedPatientsSurveys])

  useEffect(() => {
    authenticate()
  }, [authenticate])

  useEffect(() => {
    setCompletion(
      activePatientSurveys && uncompletePatientsSurveys
        ? Math.round(
            (1 -
              uncompletePatientsSurveys?.length / activePatientSurveys.length) *
              100
          )
        : 0
    )
  }, [uncompletePatientsSurveys, activePatientSurveys])

  useEffect(() => {
    scrollRef.current?.scrollIntoView({
      behavior: "smooth"
    })
  }, [patientSurveyVisible])

  const pickSurvey = (surveyReference: string) => {
    if (surveysDefinition) {
      const _survey = surveysDefinition?.find(
        (_surv) => _surv.survey_reference === surveyReference
      )
      return _survey
    }
  }

  const getBody = () => {
    switch (flowPhase) {
      case FlowPhase.INTRO:
        return renderIntro()
      case FlowPhase.SURVEYS:
        return renderSurveys()
      case FlowPhase.DOCUMENTS:
        return renderDocuments()
      case FlowPhase.OUTRO:
        return renderOutro()
      case FlowPhase.ANSWERS:
        return renderAnswers()
      case FlowPhase.STOP:
      default:
        return null
    }
  }

  const renderIntro = () => (
    <div className="flex flex-col items-center justify-center w-hull h-full overflow-auto py-4">
      <div className="flex flex-col items-center justify-center mx-4 md:mx-0 md:gap-2">
        <h1 className="font-bold text-normal text-cortexx-900 text-center mt-3">
          {t("home.intro_disclaimer")}
        </h1>
        <h2 className="font-bold text-normal text-cortexx-900 text-center">
          {t("home.intro_questions")}
        </h2>
        <h2 className="font-extralight text-normal text-cortexx-900 text-center">
          {t("home.intro_questions_explain")}
        </h2>
        <h1 className="font-bold text-normal text-cortexx-900 text-center mt-3">
          {t("home.intro_imaging")}
        </h1>
        <h2 className="font-extralight text-normal text-cortexx-900 text-center">
          {t("home.intro_prepare")}
        </h2>
        <h2 className="font-extralight text-normal text-cortexx-900 text-center">
          {t("home.intro_no_document")}
        </h2>
        <button
          className="mt-4 bg-cortexx-500 text-white font-semibold py-2 px-4 rounded-lg hover:bg-cortexx-550"
          onClick={() => {
            const firstActiveAndNotCompletedPS = activePatientSurveys?.find(
              (_ps) => !_ps.completed
            )
            if (uncompletePatientsSurveys?.length)
              setFlowPhase(FlowPhase.SURVEYS)
            else if (!medicalDocuments?.length)
              setFlowPhase(FlowPhase.DOCUMENTS)
            else setFlowPhase(FlowPhase.OUTRO)
            setPatientSurveyVisible(firstActiveAndNotCompletedPS?.id || 0)
          }}
          type="button"
        >
          {t("home.go_to_questions")}
        </button>
      </div>
    </div>
  )

  const renderSurveys = () => {
    if (!!uncompletePatientsSurveys?.length && activePatientSurveys) {
      return (
        <div className="flex flex-col w-screen h-full justify-center items-center">
          <ProgressionBar completion={completion} />
          <div className="flex snap-x snap-mandatory overflow-x-hidden w-screen md:w-[750px] h-full justify-start">
            <div className="flex flex-row">
              {activePatientSurveys.map((_patientSurvey, index) => (
                <div
                  className="flex items-center md:mx-4"
                  key={_patientSurvey.id}
                  ref={
                    _patientSurvey.id === patientSurveyVisible
                      ? scrollRef
                      : null
                  }
                >
                  <Survey
                    key={_patientSurvey.id}
                    patientSurvey={_patientSurvey}
                    patientId={account?.patient_id || 0}
                    studyId={account?.study_id || 0}
                    changeSurvey={(direction?: Direction) => {
                      if (direction === "next" || !direction) {
                        const _index =
                          completedPatientsSurveysIndexDisplayed + 1
                        setCompletedPatientsSurveysIndexDisplayed(_index)
                        setCompletion(
                          completedPatientsSurveys
                            ? Math.round(
                                (_index / activePatientSurveys.length) * 100
                              )
                            : 0
                        )
                        setPatientSurveyVisible(
                          activePatientSurveys[index + 1]?.id
                        )
                      } else if (
                        direction === "back" &&
                        completedPatientsSurveys
                      ) {
                        let _index = completedPatientsSurveysIndexDisplayed - 1
                        _index = _index >= 0 ? _index : 0
                        setCompletion(
                          completedPatientsSurveys
                            ? Math.round(
                                (_index / activePatientSurveys.length) * 100
                              )
                            : 0
                        )
                        setCompletedPatientsSurveysIndexDisplayed(_index)
                        setPatientSurveyVisible(
                          completedPatientsSurveys[_index]?.id
                        )
                      }
                    }}
                    isUp={_patientSurvey.id === patientSurveyVisible}
                  />
                </div>
              ))}
            </div>
          </div>
        </div>
      )
    }
    setFlowPhase(FlowPhase.DOCUMENTS)
    return null
  }

  const renderOutro = () => (
    <div className="flex flex-col w-screen h-full justify-center items-center overflow-auto">
      <div className="flex flex-col w-11/12 md:w-1/2 rounded-xl p-8 bg-cortexx-50 border-slate-300 border justify-center mx-4 shadow-md">
        <h1 className="font-bold text-cortexx-900 mb-2">
          {t("home.thank_you")}
        </h1>
        <h2 className="font-normal text-cortexx-900">
          {t("home.thank_you_explain")}
        </h2>
        <h1 className="font-bold text-cortexx-900 mt-6 mb-2">
          {t("home.bring_imaging")}
        </h1>
        {account?.funnel === "filter_phase_2" ? (
          <>
            <h1 className="font-bold text-cortexx-900 mt-6 mb-2">
              {t("home.confirmation_email_sent")}{" "}
              <a
                href="mailto:info@cortexxmi.com"
                className="font-normal text-cortexx-600 underline"
              >
                info@cortexxmi.com
              </a>
              .
            </h1>
            <h2 className="font-normal text-cortexx-900">
              {t("home.check_spam")}
            </h2>
            <h1 className="font-normal text-cortexx-900 mt-6 mb-2">
              {t("home.appointment_email")}{" "}
              <span className="font-bold text-cortexx-900">
                {t("home.appointment_email_bold")}
              </span>
            </h1>
          </>
        ) : null}
        <div className="flex flex-col w-full items-center justify-center">
          <button
            className="mt-8 bg-cortexx-500 text-white font-semibold py-4 px-6 rounded-lg w-full md:w-1/4"
            onClick={() => setFlowPhase(FlowPhase.ANSWERS)}
            type="button"
          >
            {t("home.go_to_summary")}
          </button>
        </div>
      </div>
    </div>
  )

  const renderAnswers = () =>
    completedPatientsSurveys?.length ? (
      <div className="flex flex-col w-full mx-4 items-center text-start h-full">
        <h1 className="text-l font-semibold text-cortexx-900 mb-4 md:w-96">
          {t("home.surveys_summary")}
        </h1>
        <div className="overflow-y-auto no-scrollbar pt-4">
          {completedPatientsSurveys.map((_patientSurvey) => (
            <div className="mb-4">
              <SurveyListCard
                patientSurvey={_patientSurvey}
                openSurvey={() => {
                  setShowSurveyModal(true)
                  setPatientSurvey(_patientSurvey)
                }}
              />
            </div>
          ))}
        </div>
      </div>
    ) : null

  const renderDocuments = () => (
    <FileUploader
      patientId={account?.patient_id?.toString() || ""}
      goNext={() => setFlowPhase(FlowPhase.OUTRO)}
    />
  )

  return (
    <div className="flex w-full bg-cortexx-50 flex-1 flex-wrap justify-center items-center h-1/2 pt-2">
      {!isPatientLoading &&
      !isUnAnsweredPatientSurveysLoading &&
      !isPatientSurveysLoading &&
      !isMedicalDocumentsLoading &&
      !isSurveysLoading ? (
        patient?.surgery_category === "no_category" ? (
          <SurgeryCategory />
        ) : (
          getBody()
        )
      ) : (
        <Loader />
      )}
      {showSurveyModal && patientSurvey ? (
        <SurveyModal
          closeModal={() => setShowSurveyModal(false)}
          survey={pickSurvey(patientSurvey.survey_reference)}
          patientSurvey={patientSurvey}
        />
      ) : null}
    </div>
  )
}

export default Home
